import type { StoreHit } from '@/lib/algolia/stores';
import { createAppointment } from '@/lib/appointment/createAppointment';
import { ACCOUNT_METADATA, getMetaValue } from '@/lib/auth/metadata';
import type { AuthUser } from '@/lib/auth/types';
import { RECAPTCHA_SITE_KEY } from '@/lib/const';
import { resolveTitleForForm, getUserTitleLabel, type TitleOption } from '@/lib/forms/titleOptions';
import { formatNationalByPhonePrefix, isValidPhoneNumber } from '@/lib/phone';
import { COUNTRY_CALLING_CODES } from '@/lib/phonePrefixes/const';
import { getPhonePrefixSelectOptByCode, handlePhonePrefix } from '@/lib/phonePrefixes/helpers';
import {
  resolveUnavailableAppointmentWeekdays,
  type Schedule,
} from '@/lib/stores/appointmentScheduling';
import { getTranslatedStoreValue } from '@/lib/stores/utils';
import { z } from 'zod/v4';

import type { AppointmentFormValues, SelectedBoutique } from './types';

const MAX_COMMENTS_LENGTH = 3000;
const MAX_PHONE_LENGTH = 20;

export type AppointmentFormTranslator = (key: string) => string;

export interface SubmitAppointmentFormInput {
  boutique: SelectedBoutique;
  values: AppointmentFormValues;
  selectedDate: string;
  countryCode: string;
  languageCode: string;
  productSku?: string;
  captchaToken: string | null;
  titleOptions: TitleOption[];
  translate: AppointmentFormTranslator;
  translateForms: AppointmentFormTranslator;
}

export type SubmitAppointmentFormResult =
  | {
      ok: true;
      fullName: string;
      appointmentId?: string;
      storeId: string;
      dateAppointment: string;
      appointmentMotive: string;
      productSku?: string;
    }
  | { ok: false; error: string; resetCaptcha: boolean };

/**
 * Zod schema for the appointment booking form.
 *
 * Example Input:
 * translate = (key) => key, translateForms = (key) => key, countryCode = "US"
 *
 * Example Output:
 * Zod schema validating title, contact fields, and phone number format
 */
export function buildAppointmentFormSchema(
  translate: AppointmentFormTranslator,
  translateForms: AppointmentFormTranslator,
  countryCode: string
) {
  const isJP = countryCode.toUpperCase() === 'JP';

  return z
    .object({
      title: z.string().min(1, translateForms('requiredField')),
      firstName: z.string().min(1, translateForms('requiredField')).max(40),
      lastName: z.string().min(1, translateForms('requiredField')).max(80),
      firstNameKanji: isJP
        ? z.string().min(1, translateForms('requiredField')).max(40)
        : z.string(),
      lastNameKanji: isJP ? z.string().min(1, translateForms('requiredField')).max(80) : z.string(),
      email: z
        .string()
        .min(1, translateForms('requiredField'))
        .email(translateForms('invalidEmailAddress')),
      phonePrefix: z.string().min(1, translate('phonePrefixRequired')),
      phone: z
        .string()
        .min(1, translate('phoneRequired'))
        .max(MAX_PHONE_LENGTH, translate('phoneMaxLength')),
      reason: z.string().min(1, translateForms('requiredField')),
      timeSlot: z.string().min(1, translate('timeSlotRequired')),
      comments: z.string().max(MAX_COMMENTS_LENGTH, translate('commentsMaxLength')),
      newsletterOptIn: z.boolean(),
      consent: z.boolean().refine((value) => value, { message: translateForms('requiredField') }),
    })
    .superRefine((values, ctx) => {
      const entry = COUNTRY_CALLING_CODES.find((item) => item.code === values.phonePrefix);
      if (!entry) return;
      const fullNumber = `${entry.value}${values.phone}`.replaceAll(/\s+/g, '');
      if (!isValidPhoneNumber(fullNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['phone'],
          message: translateForms('invalidPhoneNumber'),
        });
      }
    });
}

/**
 * Submit the appointment form to the booking API.
 *
 * Example Input:
 * boutique, form values, selectedDate, countryCode, languageCode
 *
 * Example Output:
 * { ok: true, fullName: "Jane Doe" } or { ok: false, error: "...", resetCaptcha: true }
 */
export async function submitAppointmentForm(
  input: SubmitAppointmentFormInput
): Promise<SubmitAppointmentFormResult> {
  if (RECAPTCHA_SITE_KEY && !input.captchaToken) {
    return { ok: false, error: input.translate('captchaRequired'), resetCaptcha: false };
  }

  const startDateTime = `${input.selectedDate}T${input.values.timeSlot}:00${input.boutique.utcOffset || ''}`;
  const titleOption = input.titleOptions.find((option) => option.value === input.values.title);
  const phoneCallingCode =
    COUNTRY_CALLING_CODES.find((item) => item.code === input.values.phonePrefix)?.value ?? '';
  const strippedPhone = formatNationalByPhonePrefix(
    phoneCallingCode,
    input.values.phone?.trim(),
    false
  );

  const response = await createAppointment({
    salutation: input.values.title,
    phone: `${phoneCallingCode}${strippedPhone}`,
    reason: input.values.reason,
    websiteCountryCode: input.countryCode,
    languageCode: input.languageCode,
    locationId: input.boutique.locationId,
    firstName: input.values.firstName,
    lastName: input.values.lastName,
    firstNameKanji: input.values.firstNameKanji || undefined,
    lastNameKanji: input.values.lastNameKanji || undefined,
    email: input.values.email,
    comments: input.values.comments,
    reserveProductSku: input.productSku ?? null,
    startDateTime,
    newsletterOptIn: input.values.newsletterOptIn,
    recaptchaResponse: input.captchaToken,
  });

  if (response.success) {
    const fullName =
      `${getUserTitleLabel(input.countryCode, titleOption)}${input.values.firstName} ${input.values.lastName}`.trim();
    return {
      ok: true,
      fullName,
      appointmentId: response.appointmentId,
      storeId: input.boutique.locationId,
      dateAppointment: startDateTime,
      appointmentMotive: input.values.reason,
      productSku: input.productSku,
    };
  }

  return {
    ok: false,
    error: input.translate('submitError'),
    resetCaptcha: true,
  };
}

export interface AppointmentFormFallbackDefaults {
  title: string;
  phonePrefix: string;
  reason: string;
  titleOptions: TitleOption[];
  countryCode: string;
}

/**
 * Build appointment form defaults, prefilling from a logged-in customer when available.
 *
 * Example Input:
 * user = { firstName: "Jane", lastName: "Doe", email: "jane@example.com", metadata: [...] }
 * fallbacks = { title: "Mr", phonePrefix: "US", reason: "purchase", titleOptions: [...] }
 *
 * Example Output:
 * { title: "Ms", firstName: "Jane", lastName: "Doe", email: "jane@example.com", phonePrefix: "US", phone: "5551234567", ... }
 */
export function getAppointmentFormDefaults(
  user: AuthUser | null,
  fallbacks: AppointmentFormFallbackDefaults
): AppointmentFormValues {
  const base: AppointmentFormValues = {
    title: fallbacks.title,
    firstName: '',
    lastName: '',
    firstNameKanji: '',
    lastNameKanji: '',
    email: '',
    phonePrefix: fallbacks.phonePrefix,
    phone: '',
    reason: fallbacks.reason,
    timeSlot: '',
    comments: '',
    newsletterOptIn: false,
    consent: false,
  };

  if (!user) {
    return base;
  }

  const metadata = user.metadata ?? [];
  const mappedTitle = resolveTitleForForm(
    getMetaValue(metadata, ACCOUNT_METADATA.personalTitle),
    fallbacks.countryCode
  );
  const title =
    mappedTitle && fallbacks.titleOptions.some((option) => option.value === mappedTitle)
      ? mappedTitle
      : base.title;

  const storedPhonePrefix = getMetaValue(metadata, ACCOUNT_METADATA.phonePrefix);
  const currentPrefix = handlePhonePrefix(storedPhonePrefix);
  const phoneCodeFromMetadata = currentPrefix
    ? (COUNTRY_CALLING_CODES.find(
        (entry) =>
          entry.value === currentPrefix &&
          entry.code.toUpperCase() === fallbacks.countryCode.toUpperCase()
      )?.code ?? COUNTRY_CALLING_CODES.find((entry) => entry.value === currentPrefix)?.code)
    : undefined;
  const phonePrefix = phoneCodeFromMetadata
    ? getPhonePrefixSelectOptByCode(phoneCodeFromMetadata).code
    : base.phonePrefix;

  return {
    ...base,
    title,
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    email: user.email ?? '',
    phonePrefix,
    phone: getMetaValue(metadata, ACCOUNT_METADATA.phone) ?? '',
  };
}

/**
 * Build selected boutique state from an Algolia store hit.
 *
 * Example Input:
 * store = { store_id: "US100001", name: "Gallet Boutique" }, localeSlug = "en"
 *
 * Example Output:
 * { locationId: "US100001", name: "Gallet Boutique", ... }
 */
export function buildSelectedBoutique(store: StoreHit, localeSlug: string): SelectedBoutique {
  return {
    store,
    locationId: store.store_id,
    name: getTranslatedStoreValue(store, 'name', localeSlug),
    imageUrl: store.photo_gallery?.[1]?.image?.url ?? store.photo_gallery?.[0]?.image?.url ?? '',
    appointments: (store.appointments as Schedule) ?? null,
    utcOffset: store.utc_offset ?? '',
    holidayHours: store.temporary_hours?.holiday_hours ?? [],
    reopenDate: store.temporary_hours?.reopen_date ?? undefined,
    unavailableWeekdays: resolveUnavailableAppointmentWeekdays(store),
  };
}
